import { ComponentPropsWithoutRef, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronDown, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateSegment } from 'api/segments';
import * as DropdownMenu from 'components/dropdown-menu';
import * as DeleteSegmentDialog from 'components/filter/filters-next/DeleteSegmentDialog';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import * as SaveAsNewSegmentDialog from 'components/filter/filters-next/SaveAsNewSegmentDialog';
import { SegmentContext } from 'components/filter/filters-next/SegmentContext';
import { SegmentNameForm } from 'components/filter/filters-next/SegmentNameForm';
import { defaultFilterGroups } from 'components/filter/filters-next/defaults';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ModelType } from 'components/filter/types-legacy';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentSegment } from 'customHooks/useCurrentSegment';
import { segmentQueryKeys } from 'queries/segment-query-keys';
import { twMerge } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

const SegmentBar = (props: Omit<ComponentPropsWithoutRef<'div'>, 'children'>) => {
    const { className = '', ...rest } = props;
    const isViewingSegment = useCurrentSegment();
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { cacheKey, resource } = useContext(FilterProviderContext);
    const { name, isEditing, setIsEditing } = useContext(SegmentContext);
    const { filters } = useFiltersByKey(cacheKey);
    const areFiltersEmpty = filters.length === 0;
    const setSegmentId = useFiltersCache(state => state.setSegmentIdByKey);
    const setFilters = useFiltersCache(state => state.updateFiltersNextByKey);
    const queryClient = useQueryClient();

    const { mutate: saveSegment } = useMutation({
        mutationFn: () =>
            updateSegment({
                accountId,
                segmentId: isViewingSegment?.id as number,
                name,
                type: resource as ModelType,
                filters,
            }),
        onSuccess: () => {
            success('Updated segment successfully!');
            setIsEditing(false);
            queryClient.invalidateQueries({
                queryKey: segmentQueryKeys.all(accountId, resource as ModelType),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? `Unable to save segment`);
        },
    });

    if (!isViewingSegment) {
        return null;
    }

    return (
        <div className={twMerge('flex items-center justify-between gap-4', className)} {...rest}>
            <SegmentNameForm />
            <div className={'flex items-center gap-2'}>
                {isEditing && (
                    <Button
                        variant="none"
                        size="sm"
                        color="neutral"
                        className="px-2 font-medium"
                        onClick={() => {
                            setIsEditing(false);
                        }}
                    >
                        Cancel
                    </Button>
                )}
                {!isEditing && (
                    <Button
                        variant="none"
                        size="sm"
                        color="neutral"
                        className="px-2 font-medium"
                        onClick={() => {
                            setIsEditing(false);
                            setSegmentId(cacheKey);
                            setFilters(cacheKey, defaultFilterGroups);
                        }}
                    >
                        Clear
                    </Button>
                )}
                {isEditing && (
                    <>
                        <DeleteSegmentDialog.Root segmentId={isViewingSegment.id}>
                            <DeleteSegmentDialog.Trigger asChild={true}>
                                <Button size={'sm'} variant={'outline'} color={'neutral'}>
                                    <FontAwesomeIcon icon={faTrash} />
                                    Delete
                                </Button>
                            </DeleteSegmentDialog.Trigger>
                        </DeleteSegmentDialog.Root>
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild={true} disabled={areFiltersEmpty}>
                                <Button size={'sm'} variant={'outline'} color={'neutral'}>
                                    Save
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align={'end'} sideOffset={4}>
                                <DropdownMenu.Item
                                    onClick={() => {
                                        setIsEditing(false);
                                        saveSegment();
                                    }}
                                >
                                    Save
                                </DropdownMenu.Item>
                                <SaveAsNewSegmentDialog.Root>
                                    <SaveAsNewSegmentDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Save as new segment
                                        </DropdownMenu.Item>
                                    </SaveAsNewSegmentDialog.Trigger>
                                </SaveAsNewSegmentDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </>
                )}
            </div>
        </div>
    );
};

export { SegmentBar };
